/**
 * usePriorityRenderers.ts
 *
 * Registers CopilotKit v2 tool renderers for all OneThing priority tools.
 * Call this hook once inside CopilotToolRenderers (inside <CopilotKitProvider>).
 * Returns nothing — side-effects only.
 */

import { useRenderTool } from "@copilotkit/react-core/v2";
import { createElement } from "react";
import { PriorityListCard, type PriorityListRow } from "../cards/PriorityListCard";
import { PriorityDetailCard, type PriorityDetail } from "../cards/PriorityDetailCard";
import { MutationResultCard, type MutationData } from "../cards/MutationResultCard";
import {
  type ToolRenderProps,
  LoadingCard,
  ErrorCard,
  RawCard,
  resolveEnvelope,
} from "./renderHelpers";

// Shared render function for both priority-list tools (T01 + T02)
function renderPriorityList(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Loading priorities…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok && env.kind === "priority_list") {
    return createElement(PriorityListCard, {
      summary: env.summary,
      rows: Array.isArray(env.data) ? (env.data as PriorityListRow[]) : [],
    });
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

function renderPriorityDetail(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Loading priority…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok && env.kind === "priority_detail") {
    return createElement(PriorityDetailCard, { data: env.data as PriorityDetail });
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

// Shared render fn for all priority mutation tools (T18–T24) — DRY
function renderPriorityMutation(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Updating priority…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok && env.kind === "priority_mutation") {
    return createElement(MutationResultCard, {
      kind: "priority_mutation",
      summary: env.summary,
      data: env.data as MutationData,
    });
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

export function usePriorityRenderers(): void {
  // T01 — user's own priorities
  useRenderTool({ name: "onething__get_user_priorities", render: renderPriorityList }, []);
  // T02 — team priorities (same kind, same card)
  useRenderTool({ name: "onething__team_priorities", render: renderPriorityList }, []);
  useRenderTool({ name: "onething__list_deliverables", render: renderPriorityList }, []);
  // T03 — single priority detail
  useRenderTool({ name: "onething__get_priority", render: renderPriorityDetail }, []);
  // T18–T24 — priority mutations (all emit priority_mutation kind)
  useRenderTool({ name: "onething__create_priority", render: renderPriorityMutation }, []);
  useRenderTool({ name: "onething__update_priority_info", render: renderPriorityMutation }, []);
  useRenderTool({ name: "onething__update_priority_dri", render: renderPriorityMutation }, []);
  useRenderTool({ name: "onething__update_priority_status", render: renderPriorityMutation }, []);
  useRenderTool({ name: "onething__add_priority_assignee", render: renderPriorityMutation }, []);
  useRenderTool({ name: "onething__remove_priority_assignee", render: renderPriorityMutation }, []);
  useRenderTool({ name: "onething__swap_focus_priority", render: renderPriorityMutation }, []);
}
